import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroller';
import { useTranslation } from "react-i18next";
import DOMPurify from 'dompurify';
import { useDebounceCallback } from '@react-hook/debounce';
import Debounce from '../common/utils/Debounce';

import { Stack, Typography, Box } from '@mui/material';

import { p1, py2, flexRowCentered, flexColCentered, flexColFullHeightScrollable } from "../common/ui/sx/common";
import { useAuth } from '../common/hooks/useAuth';
import SearchContext from './SearchContext';
import ProductCard from '../common/ui/qvshop/components/ProductCard';
import NoLocationSelectedDialog from '../common/ui/qvshop/components/popups/NoLocationSelectedDialog';
import { usePageLayoutConfigurator } from '../common/hooks/usePageLayoutConfigurator';
import ProductCardSkeleton from '../common/ui/qvshop/components/ProductCardSkeleton';
import MasonrySection from '../common/ui/qvshop/components/MasonrySection';

import ShopConfig from '../common/api/config/shopConfig';
import PageNames from '../common/utils/PageNames';
import { CategoriesUtils } from '../common/utils/CategoriesUtils';

import { selectNoLocationSelected, selectStartDate, selectEndDate, selectRecurrenceType, selectStartDateRecurring, selectEndDateRecurring, selectRepetitionInterval, selectRecurrenceStartDay, selectRecurrenceStartTime, selectRecurrenceEndDay, selectRecurrenceEndTime, selectRecurrenceHasErrors, selectProductsAreFiltered, selectCategory, setCategory, setCategoryPath, selectFromToHasErrors, selectRecurring, selectLocation } from '../common/store/filtersSlice';
import { selectIsLoadingUserLocations, selectLocationSelectionRequired } from '../common/store/locationsSlice';
import { selectIsLoading, selectStatusApiHasFailed, getProducts, selectProducts, selectProductsCount, checkAvailabilityForProducts, selectTriggerProductsAvailability, getProductsCount } from '../common/store/productsSlice';
import { selectHasWelcomeImage, selectHasWelcomeText, selectWelcomeImage, selectWelcomeText, selectSettingsLoaded } from '../common/store/settingsSlice';
import { selectCartHasItems } from '../common/store/cartSlice';
import { selectCategories } from '../common/store/categoriesSlice';
import { removeProduct } from '../common/store/productSlice';

export default function ImageMasonry() {
    usePageLayoutConfigurator(PageNames.PRODUCTS_LIST);

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { token } = useAuth();
    let { categoryId } = useParams();
    const category = categoryId ? categoryId : '';

    const showWelcomeText = useSelector(selectHasWelcomeText);
    const welcomeText = useSelector(selectWelcomeText);
    const showWelcomeImage = useSelector(selectHasWelcomeImage);
    const welcomeImage = useSelector(selectWelcomeImage);
    const selectedCategory = useSelector(selectCategory);
    const selectedLocation = useSelector(selectLocation);
    const categories = useSelector(selectCategories);
    
    // Apply category-based filtering only if the setting is enabled
    const shouldShowArticles = true; // !showArticlesOnlyWhenCategorySelected || hasCategorySelected;

    const noLocationSelected = useSelector(selectNoLocationSelected);
    const locationSelectionRequired = useSelector(selectLocationSelectionRequired);
    const isLoadingUserLocations = useSelector(selectIsLoadingUserLocations);
    const settingsLoaded = useSelector(selectSettingsLoaded);

    const isLoadingProducts = useSelector(selectIsLoading);
    const statusApiHasFailed = useSelector(selectStatusApiHasFailed);
    const products = useSelector(selectProducts);
    const productsCount = useSelector(selectProductsCount);
    const productsAreFiltered = useSelector(selectProductsAreFiltered);

    const cartHasItems = useSelector(selectCartHasItems);
    const triggerAvailability = useSelector(selectTriggerProductsAvailability);

    const recurring = useSelector(selectRecurring);
    const startDate = useSelector(selectStartDate);
    const endDate = useSelector(selectEndDate);
    const fromToHasErrors = useSelector(selectFromToHasErrors);

    const recurrenceType = useSelector(selectRecurrenceType);
    const startDateRecurring = useSelector(selectStartDateRecurring);
    const endDateRecurring = useSelector(selectEndDateRecurring);
    const repetitionInterval = useSelector(selectRepetitionInterval);
    const recurrenceStartDay = useSelector(selectRecurrenceStartDay);
    const recurrenceStartTime = useSelector(selectRecurrenceStartTime);
    const recurrenceEndDay = useSelector(selectRecurrenceEndDay);
    const recurrenceEndTime = useSelector(selectRecurrenceEndTime);
    const recurrenceHasErrors = useSelector(selectRecurrenceHasErrors);
    const lastProductsCountRequest = useRef(null);
    const searchActive = useSelector(state => state.filters.search);
    const searchRevision = useSelector(state => state.filters.searchRevision);
    const specificationsFilters = useSelector(state => state.filters.specificationsFilters);
    const generation = useSelector(state => state.products.generation);
    const countReady = useSelector(state => state.products.countReady);
    const productsStatus = useSelector(state => state.products.status);

    const debouncedGetProductsCount = useDebounceCallback(
        (requestKey) => {
            if (!(locationSelectionRequired && noLocationSelected)) {
                // Only remember requests that actually start; StrictMode can cancel the timer.
                lastProductsCountRequest.current = requestKey;
                dispatch(getProductsCount({}));
            }
        },
        // delay in ms
        Debounce.DEBOUNCE_MIN_TIMEOUT
    );

    useEffect(() => {
        // Remove product details if it was previously loaded
        dispatch(removeProduct());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!(/^\d+$/.test(category)) && category !== "") {
            navigate("/items/", { replace: true });
            return;
        }

        dispatch(setCategoryPath(CategoriesUtils.prepareCategoryPath(categories, category)));

        // If the category is changed (from new URL path or by selecting other category) we need to get the products count
        if (!settingsLoaded || (token && isLoadingUserLocations)) {
            return;
        }

        if (selectedCategory !== category) {
            dispatch(setCategory(category));
            return;
        }

        if (!startDate || !endDate || fromToHasErrors || recurrenceHasErrors ||
            (locationSelectionRequired && noLocationSelected)) {
            lastProductsCountRequest.current = null;
            return;
        }

        const requestKey = JSON.stringify([category, selectedLocation, startDate, endDate,
            searchRevision, specificationsFilters, token]);
        if (lastProductsCountRequest.current !== requestKey || productsStatus === 'idle') {
            debouncedGetProductsCount(requestKey);
        }
    }, [dispatch, category, selectedCategory, startDate, endDate, debouncedGetProductsCount,
        navigate, categories, selectedLocation, settingsLoaded, isLoadingUserLocations,
        locationSelectionRequired, noLocationSelected, token, searchRevision,
        specificationsFilters, fromToHasErrors, recurrenceHasErrors, productsStatus]);

    const debouncedAvailabilityCheck = useDebounceCallback(
        (products) => {
            if (products) {
                dispatch(checkAvailabilityForProducts());
            }
        },
        // delay in ms
        Debounce.DEBOUNCE_TIMEOUT
    );

    useEffect(() => {
        // console.log("useEffect: debouncedAvailabilityCheck, fromToHasErrors: ", fromToHasErrors);

        if (startDate && endDate && (!recurrenceHasErrors) && (!fromToHasErrors)) {
            debouncedAvailabilityCheck(products);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, debouncedAvailabilityCheck, cartHasItems, triggerAvailability, recurring, 
        startDate, endDate, recurrenceType, startDateRecurring, endDateRecurring, 
        repetitionInterval, recurrenceStartDay, recurrenceStartTime, recurrenceEndDay, 
        recurrenceEndTime, recurrenceHasErrors, fromToHasErrors]);

    const handleCancelSelectLocation = () => {
        console.log("handleCancelSelectLocation called.");
    }

    // Reset pagination for every new result set, even when the count stays the same.
    const pagination = useRef({ generation: null, page: 0 });
    if (pagination.current.generation !== generation) {
        pagination.current = { generation, page: 0 };
    }
    const handleLoadMore = (page) => {
        // if locations are defined and no location is selected, do not load more products
        if (locationSelectionRequired && noLocationSelected) {
            return;
        }

        // load more products if not loading already, and avoid loading the same page twice
        if (countReady && !isLoadingProducts && pagination.current.page !== page) {
            dispatch(getProducts({ page }));

            pagination.current.page = page;
        }
    }

    const productsLoadedCount = products ? Object.values(products).length : 0;

    // isLoadingProducts is needed here to put hasMore on false while loading more products
    // this fixes the problem that handleLoadMore is called with a page number while still loading the page before
    // this causes the page to be ignored (handleLoadMore did not load that page but the infinite scroll does not take that into account)
    const hasMore = countReady && !statusApiHasFailed && !isLoadingProducts && productsLoadedCount < productsCount;

    // todo: add padding using the css class for columns, see example in docs
    return (
        <Stack sx={{...flexColFullHeightScrollable, ...p1}} direction='column' spacing={2}>
            {!productsAreFiltered && <Box sx={{...flexColCentered}}>
                    { showWelcomeImage &&
                        <Box
                        component="img"
                        sx={{
                            display: 'block',
                            width: '100%',
                            height: 'auto',
                            minHeight: '100px',
                            maxHeight: '500px',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            borderRadius: '10px'
                        }}
                        alt="product list"
                        src={ShopConfig.config().getURLForWelcomeImage(welcomeImage)}
                    /> }
                    { showWelcomeText && 
                        <Box sx={{...flexRowCentered, ...py2}}>
                            <Typography dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(welcomeText) }} ></Typography>
                        </Box>
                    }
            </Box>}

            <SearchContext invalidDates={fromToHasErrors || recurrenceHasErrors} />

            {productsAreFiltered && !searchActive &&
                <Box sx={{ ...flexRowCentered, ...py2 }}>
                    <Typography variant="h5">{productsCount + t("product.list.searchResults")}</Typography>
                </Box>
            }

            {!shouldShowArticles ? (
                <Box sx={{ ...flexRowCentered, ...py2 }}>
                    <Typography variant="h6" color="text.secondary">
                        {t("product.list.selectCategoryMessage")}
                    </Typography>
                </Box>
            ) : (
                <InfiniteScroll
                    key={generation}
                    pageStart={0}
                    loadMore={handleLoadMore}
                    hasMore={hasMore}
                    loader={!(locationSelectionRequired && noLocationSelected) ? <div className="loader" key={0}><Typography variant="body">Loading ...</Typography></div> : null}
                    useWindow={false}
                >
                    <MasonrySection breakpointCols={{
                        default: 5,
                        980: 1,
                        1310: 2,
                        1640: 3,
                        1960: 4,
                        2290: 5
                    }}
                        items={(products ? Object.values(products) : [])}
                        loading={isLoadingProducts}
                        skeleton={ProductCardSkeleton}
                        card={ProductCard} />
                </InfiniteScroll>
            )}
            {locationSelectionRequired && noLocationSelected && <NoLocationSelectedDialog open={noLocationSelected} onOk={() => { }} onCancel={handleCancelSelectLocation} showActionBar={false} />}
        </Stack>
    );
}
